import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios'
import { parseRequestError } from '@/common/utilities.ts'
import { Role } from '@/common/types_and_interfaces.ts'
import { ENV, notAllowedRoles } from '@/common/constants.ts'
import { z } from 'zod'
import { RequestResponse } from '@/common/types_and_interfaces.ts'
import { RootState } from '../store.ts'
import { AUTH_MODE, keycloakAuthService } from '@/auth/keycloak'

const MILI_SECONDS_PER_SECOND = 1000 as const
const BUFFER_SECONDS = 8 as const

interface UserData {
  id: number
  status: number
  first_name: string | null
  last_name: string | null
  phone: string | null
  email: string | null
  date_of_birth: string | null
  city: string | null
  roles_resource_permissions: object
  profile_picture: string | null
  language_preference_id: number | null
  global_timezone_id: number | null
  global_state_id: number | null
  global_country_id: number | null
  enrolled_programs: Array<EnrolledProgram> | null
}

interface EnrolledProgram {
  id: number
  status: number
  name: string | null
  state_code: string | null
  state_name: string | null
}

interface LoginResponse {
  user: UserData | null
  access_token: string
  expires_at: number
  phone: string | null | undefined
  message: string | null | undefined
  rememberMe: boolean | null | undefined
}

interface AuthState {
  loading: boolean
  authStatus: 'idle' | 'loading' | 'succeeded' | 'failed'
  error: string | null
  isAuthenticated: boolean
  is2FAPending: boolean
  phone: string | null
  access_token: string | null
  expires_at: number
  user: UserData | null
  programs: number[]
  roles: string[]
  privileges: string[]
  privilegesStatus: 'idle' | 'loading' | 'succeeded' | 'failed'
  rememberMeToken: string | null
}

const initialState: AuthState = {
  loading: false,
  authStatus: 'idle',
  error: null,
  isAuthenticated: false,
  is2FAPending: false,
  phone: null,
  access_token: null,
  expires_at: 0,
  user: null,
  programs: [],
  roles: [],
  privileges: [],
  privilegesStatus: 'idle',
  rememberMeToken: null,
}

const AuthResponseSchema = z.object({
  access_token: z.string(),
  token_type: z.string(),
  expires_in: z.number(),
  message: z.string().optional().nullable(),
  phone: z.string().optional().nullable(),
})

type AuthResponse = z.infer<typeof AuthResponseSchema>

export const AuthenticateUser = async (
  email: string,
  password: string,
  remember_me: boolean
): Promise<RequestResponse<AuthResponse>> => {
  try {
    const response = await axios.post(
      `${ENV.VITE_API_URL}/access/admins/login`,
      {
        email,
        password,
        remember_me,
      }
    )
    const cleanResponse = AuthResponseSchema.parse(response.data)
    return { data: cleanResponse, error: null }
  } catch (error) {
    return { data: null, error: parseRequestError(error) }
  }
}

export const bootstrapFromKeycloak = createAsyncThunk<
  LoginResponse,
  void,
  { rejectValue: string }
>('auth/bootstrapFromKeycloak', async (_, { rejectWithValue }) => {
  try {
    if (AUTH_MODE !== 'keycloak') {
      return rejectWithValue('Not in keycloak mode')
    }

    await keycloakAuthService().ensureFreshToken(30)
    const token = keycloakAuthService().getAccessToken()
    const tokenParsed = keycloakAuthService().getTokenParsed()
    if (!token) return rejectWithValue('Missing KC token')

    // ask your API who I am (same endpoint you already use)
    const userResponse = await axios.get(`auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })

    // derive expiry from KC token
    const expMs = (tokenParsed?.exp ?? 0) * 1000
    const now = Date.now()
    const expires_in = Math.max(0, Math.floor((expMs - now) / 1000)) // secs

    return {
      access_token: token,
      expires_at: now + (expires_in - 8) * 1000, // small buffer
      user: userResponse.data,
      phone: null,
      message: null,
      rememberMe: false,
    }
  } catch (err) {
    return rejectWithValue('Unable to bootstrap from Keycloak')
  }
})

// Async thunk for login
export const requestLogin = createAsyncThunk<
  LoginResponse,
  { email: string; password: string; remember_me: boolean },
  { rejectValue: string }
>('auth/requestLogin', async (credentials, { rejectWithValue }) => {
  try {
    const loginResponse = await AuthenticateUser(
      credentials.email,
      credentials.password,
      credentials.remember_me
    )

    if (loginResponse.error) {
      return rejectWithValue(loginResponse.error.message)
    }

    const userResponse = await axios.get(`auth/me`, {
      headers: {
        Authorization: `Bearer ${loginResponse.data.access_token}`,
      },
    })

    // We remove a couple of seconds from the ttl (expires_at) timestamp to account for network delay
    if (
      userResponse.data.roles_resource_permissions.find((role: Role) =>
        notAllowedRoles.includes(role.code)
      )
    ) {
      return rejectWithValue('Role has no access to Ace Admin Portal')
    }

    // We remove a couple of seconds from the ttl (expires_at) timestamp to account for network delay
    return {
      access_token: loginResponse.data.access_token,
      expires_at:
        Date.now() +
        (loginResponse.data.expires_in - BUFFER_SECONDS) *
          MILI_SECONDS_PER_SECOND,
      user: userResponse.data,
      phone: loginResponse.data.phone,
      message: loginResponse.data.message,
      rememberMe: credentials.remember_me,
    }
  } catch (error) {
    const parsedError = parseRequestError(error)
    return rejectWithValue(parsedError.message)
  }
})

// Async thunk for refreshing the token
export const refreshMe = createAsyncThunk('auth/me', async () => {
  try {
    const userResponse = await axios.get(`auth/me`)
    return {
      user: userResponse.data,
    }
  } catch (error) {
    /* empty */
  }
})

// Async thunk for refreshing the token
export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { rejectWithValue }) => {
    // const state = getState() as { auth: AuthState }
    // const { access_token } = state.auth
    //
    // if (!access_token) {
    //   return rejectWithValue('Refresh token missing')
    // }

    try {
      const response = await axios.post(`auth/refresh`)
      const cleanResponse = AuthResponseSchema.parse(response.data)
      return {
        access_token: cleanResponse.access_token,
        expires_at:
          Date.now() + cleanResponse.expires_in * MILI_SECONDS_PER_SECOND,
      }
    } catch (error) {
      const parsedError = parseRequestError(error)
      return rejectWithValue(parsedError.message)
    }
  }
)

// Async thunk for 2FA verification
export const verify2FA = createAsyncThunk(
  'auth/verify2FA',
  async (code: string, { rejectWithValue }) => {
    try {
      const response = await axios.post(`access/admins/login`, { code }) // Update with your API
      return response.data
    } catch (error) {
      return rejectWithValue(parseRequestError(error).message)
    }
  }
)

// Async thunk auth
export const fetchUserPrivileges = createAsyncThunk(
  'users/fetchPrivileges',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('users/privileges')
      return response.data
    } catch (error) {
      return rejectWithValue(parseRequestError(error).message)
    }
  }
)

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.isAuthenticated = false
      state.is2FAPending = false
      state.access_token = null
      state.user = null
      state.privileges = []
      state.roles = []
      state.programs = []
      state.privilegesStatus = 'idle'
      state.rememberMeToken = null
      state.authStatus = 'idle'
    },
    removeError: (state) => {
      state.error = null
    },
    setUserData: (state, action) => {
      state.user = action.payload
    },
    setVerified: (state) => {
      state.isAuthenticated = true
      state.is2FAPending = false
    },
    resetAuthState: (state) => {
      state.authStatus = 'idle'
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(requestLogin.pending, (state: AuthState) => {
        state.loading = true
        state.error = null
        state.authStatus = 'loading'
      })
      .addCase(requestLogin.fulfilled, (state: AuthState, action) => {
        state.loading = false
        state.is2FAPending = false // This option will be disabled according to Riv
        state.isAuthenticated = true // User is logged in
        state.access_token = action.payload.access_token
        state.user = action.payload.user
        state.expires_at = action.payload.expires_at
        if (
          action.payload.message == '2FA required' &&
          action.payload.phone != undefined
        ) {
          state.isAuthenticated = false
          state.is2FAPending = true
          state.phone = action.payload.phone
        }
        if (action.payload.rememberMe) {
          state.rememberMeToken = action.payload.access_token
        }
        state.authStatus = 'succeeded'
      })
      .addCase(requestLogin.rejected, (state: AuthState, action) => {
        state.loading = false
        state.error = action.payload as string
        state.authStatus = 'failed'
      })

      // Refresh Token
      .addCase(refreshToken.pending, (state: AuthState) => {
        state.loading = true
        state.error = null
      })
      .addCase(refreshToken.fulfilled, (state: AuthState, action) => {
        state.loading = false
        state.isAuthenticated = true
        state.access_token = action.payload.access_token // Update the access token
        state.expires_at = action.payload.expires_at
      })
      .addCase(refreshToken.rejected, (state: AuthState, action) => {
        state.loading = false
        state.isAuthenticated = false
        state.access_token = null
        state.error = action.payload as string
      })

      // 2FA
      .addCase(verify2FA.pending, (state: AuthState) => {
        state.loading = true
        state.error = null
      })
      .addCase(verify2FA.fulfilled, (state: AuthState) => {
        state.loading = false
        state.isAuthenticated = true // User is logged in
        state.is2FAPending = false // Reset 2FA status
      })
      .addCase(verify2FA.rejected, (state: AuthState, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      .addCase(refreshMe.fulfilled, (state: AuthState, action) => {
        if (action.payload) state.user = action.payload.user
      })

      // Fetch Privileges
      .addCase(fetchUserPrivileges.pending, (state: AuthState) => {
        state.loading = true
        state.privilegesStatus = 'loading'
        state.error = null
      })
      .addCase(fetchUserPrivileges.fulfilled, (state: AuthState, action) => {
        state.loading = false
        state.privilegesStatus = 'succeeded'
        state.privileges = action.payload.privileges || []
        state.roles = action.payload.roles || []
        state.programs = action.payload.programs || []
      })
      .addCase(fetchUserPrivileges.rejected, (state: AuthState, action) => {
        state.loading = false
        state.privilegesStatus = 'failed'
        state.error = action.payload as string
      })
      .addCase(bootstrapFromKeycloak.pending, (state) => {
        state.loading = true
        state.error = null
        state.authStatus = 'loading'
      })
      .addCase(bootstrapFromKeycloak.fulfilled, (state, action) => {
        state.loading = false
        state.is2FAPending = false
        state.isAuthenticated = true
        state.access_token = action.payload.access_token
        state.user = action.payload.user
        state.expires_at = action.payload.expires_at
        state.authStatus = 'succeeded'
      })
      .addCase(bootstrapFromKeycloak.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
        state.authStatus = 'failed'
      })
  },
})

export const { logout, removeError, setUserData, setVerified, resetAuthState } =
  authSlice.actions
export default authSlice.reducer

export const selectUser = (state: RootState) => {
  if (!state?.auth?.user?.id) {
    throw new Error('User is not authenticated')
  }
  return state.auth
}
